import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { TaiwanDiscountTableRow } from '../../../../../core/models/parking/taiwan-discount.model';
import { ApiTaiwanDiscountService } from '../../../../../core/services/api/parking/api-taiwan-discount.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalTaiwanDiscountPreviewComponent } from '../../../modals/parking/modal-taiwan-discount-preview/modal-taiwan-discount-preview.component';

@Component({
  selector: 'app-taiwan-discount',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalTaiwanDiscountPreviewComponent],
  templateUrl: './taiwan-discount.component.html',
  styleUrl: './taiwan-discount.component.css',
})
export class TaiwanDiscountComponent implements OnInit {
  private readonly api = inject(ApiTaiwanDiscountService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<TaiwanDiscountTableRow[]>([]);
  isLoading = signal(false);
  isPreviewOpen = signal(false);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(1);
  lastFetchedTime: Date | null = null;

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  ngOnInit(): void {
    this.loadTable();
  }

  onSearchChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  openPreview(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์นำเข้า');
      return;
    }
    this.isPreviewOpen.set(true);
  }

  closePreview(): void {
    this.isPreviewOpen.set(false);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  trackById(_index: number, item: TaiwanDiscountTableRow): number {
    return item.id;
  }

  loadTable(): void {
    this.isLoading.set(true);
    this.api.getTaiwanDiscountForTable({
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: this.searchText().trim() || undefined,
    }).subscribe({
      next: (res) => {
        const raw = res as Record<string, unknown>;
        const list = (raw['data'] ?? raw['Data'] ?? []) as Record<string, unknown>[];
        this.items.set(list.map((r) => ({
          id: Number(r['id'] ?? r['Id'] ?? 0),
          name: String(r['name'] ?? r['Name'] ?? ''),
          uuid: String(r['uuid'] ?? r['Uuid'] ?? ''),
          isOnline: !!(r['isOnline'] ?? r['IsOnline']),
          carCategory: Number(r['carCategory'] ?? r['CarCategory'] ?? 0),
          type: Number(r['type'] ?? r['Type'] ?? 0),
          active: !!(r['active'] ?? r['Active'] ?? false),
          taiwanCreatedAt: (r['taiwanCreatedAt'] ?? r['TaiwanCreatedAt'] ?? null) as string | null,
          taiwanUpdatedAt: (r['taiwanUpdatedAt'] ?? r['TaiwanUpdatedAt'] ?? null) as string | null,
          lastSyncedAt: (r['lastSyncedAt'] ?? r['LastSyncedAt'] ?? null) as string | null,
        })));
        this.totalItems.set(Number(raw['total'] ?? raw['Total'] ?? 0));
        this.totalPages.set(Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลล้มเหลว');
      },
    });
  }
}
